import React, { BaseSyntheticEvent, FormEvent, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'
import dynamic from 'next/dynamic'

import {
  CreateCommentInput,
  Readable,
  Comment,
  useCreateCommentMutation,
  CreateCommentMutation,
} from 'generated/apolloComponents'
import useTranslation from 'next-translate/useTranslation'
import { isEmpty } from 'lodash'
import Avatar from '../../atoms/Avatar'
import useModal from '@/hooks/useModal'
import Modal from '../../molecules/Modal'
import AuthContent from '../AuthContent'
import { socialsLogin } from '@/data/auth'

type Variables = {
  input: CreateCommentInput
}

interface IProps {
  readable: Readable
  updateCommentsList: (comment: Comment) => void
  commentId?: number
}

const CommentForm = ({ readable, updateCommentsList, commentId }: IProps) => {
  const [preview, setPreview] = useState<string | null>(null)
  const [errors, setErrors] = useState<string | null>(null)
  const [active, setActive] = useState(false)
  const commentValue = useRef() as React.MutableRefObject<HTMLTextAreaElement>
  const { data: session } = useSession()
  const { isOpen, closeModal, openModal } = useModal()
  const { t, lang } = useTranslation('common')

  const [createComment] = useCreateCommentMutation({
    onCompleted: (data: CreateCommentMutation) => {
      if (isEmpty(data?.createComment?.errors)) {
        commentValue.current.value = ''
        updateCommentsList(data?.createComment as Comment)
      } else {
        setErrors(data?.createComment?.errors)
      }
    },
  })

  const handlePreview = () => {
    if (!preview) {
      setPreview(commentValue.current.value)
    } else {
      setPreview(null)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const variables: Variables = {
      input: {
        markdownContent: commentValue.current.value,
        readableId: readable.id,
      },
    }

    if (session) {
      if (commentId) variables.input.parentCommentId = commentId.toString()
      createComment({ variables: variables })
    }
  }

  const handleCommentClick = (e: BaseSyntheticEvent) => {
    if (!session) {
      openModal()
    }
    setActive(true)
  }

  const handleHelpClick = (e: BaseSyntheticEvent) => {
    if (session) {
      openModal()
    }
  }

  const MarkdownPreview = dynamic(
    () => import('../../atoms/CommentPreview'),
    {}
  )

  const Help = dynamic(() => import('../../molecules/Help'), {})

  return (
    <>
      {!session && (
        <Modal isOpen={isOpen} closeModal={closeModal}>
          <AuthContent
            data={socialsLogin.map((item) => ({
              ...item,
              title: t(`common:${item.title}`),
            }))}
            title={t('common:login_to_bulldog')}
          />
        </Modal>
      )}
      {session && (
        <Modal
          isOpen={isOpen}
          closeModal={closeModal}
          className='py-6 px-6 max-w-4xl'
        >
          <Help />
        </Modal>
      )}
      <header className='flex items-center'>
        {session?.user?.image ? (
          <span className={` rounded-full`}>
            <Avatar url={session?.user?.image} remote />
          </span>
        ) : null}
        <p className='ml-4 text-xl'>{session?.user?.name}</p>
      </header>
      <form className='mt-4' onSubmit={handleSubmit}>
        <div onClick={handleCommentClick}>
          <textarea
            ref={commentValue}
            disabled={!session}
            style={active ? { height: '200px' } : {}}
            className={`bg-gray-100  w-full rounded-xl py-4 md:py-8 px-5 md:px-12  placeholder-font-medium placeholder-black text-xl transition-all ${
              !session ? 'cursor-pointer' : 'cursor-text'
            } ${preview ? 'hidden' : ''}`}
            placeholder={t('common:thinking_about')}
          />
          {errors &&
            Object.keys(errors).map((item) =>
              // @ts-ignore
              errors[item].map((error, index) => (
                <p className='text-error font-normal text-sm mb-5' key={index}>
                  {error}
                </p>
              ))
            )}
        </div>

        <div
          className={`border border-gray-100 py-8 px-12 mb-4 rounded-xl ${
            !preview ? 'hidden' : ''
          }`}
        >
          <MarkdownPreview preview={preview!} />
        </div>

        {session && (
          <div className='flex mt-2'>
            <button
              type='submit'
              className='bg-black rounded-full py-1 md:py-3 px-6 md:px-14 text-white font-normal hover:bg-gray-500 transition-colors'
            >
              {t('common:comment')}
            </button>
            <button
              onClick={handlePreview}
              type='button'
              className='ml-auto bg-gray-100 rounded-full py-1 md:py-3 px-6 md:px-14 hover:bg-gray-500 transition-all hover:text-white'
            >
              {!preview ? t('common:preview') : t('common:edit')}
            </button>
            <button
              type='button'
              onClick={handleHelpClick}
              className='ml-2 bg-gray-100 rounded-full py-1 md:py-3 px-6 md:px-14 hover:bg-gray-500 transition-all hover:text-white'
            >
              {t('common:help')}
            </button>
          </div>
        )}
      </form>
    </>
  )
}

export default CommentForm
